/** @format */

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { IJoinRequestPayload } from "../../../utility/types/joinRequestType";
import { joinCircleRequest } from "../../../redux/actions/circleAction";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	TextField,
	DialogActions,
	Button,
	Alert,
} from "@mui/material";
import { colors } from "../../../utility/types/constant";
import { selectAllCircles } from "../../../redux/selectors/circleSelector";
import { BootstrapDialog } from "../notification-modal/styles";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import {
	checkCircleCountryEligibility,
	getCountryEligibilityMessage,
} from "../../../utility/circleCountryEligibility";
import { trackJoinCircle } from "../../../utility/redditPixel";

interface IJoinRequestFormModalProps {
	circleId: string;
	open: boolean;
	onClose: () => void;
	sx?: any;
	onActionComplete?: () => void;
}

const JoinRequestForm: React.FC<IJoinRequestFormModalProps> = ({
	circleId,
	open,
	onClose,
	sx,
	onActionComplete,
}) => {
	const [reason, setReason] = useState<string>("");
	const dispatch = useDispatch<AppDispatch>();
	const userData = useSelector(selectCurrentUser);
	const circles = useSelector(selectAllCircles) || [];
	const [showLoading, setShowLoading] = useState(false);

	const filteredCircle = circles.filter((c: any) => c._id === circleId);
	const countryEligibility = checkCircleCountryEligibility(
		userData,
		filteredCircle[0],
	);
	const canJoin = countryEligibility.eligible;

	const currentCircleName = filteredCircle.map((c: any) => c.circleName);

	const handleSendJoinRequest = async () => {
		setShowLoading(true);
		try {
			const payload: IJoinRequestPayload = {
				userId: userData?._id || "",
				reason: reason,
			};

			await dispatch(joinCircleRequest({ payload, circleId })).unwrap();
			trackJoinCircle(`${payload.userId}-${circleId}`);
			onClose();
			onActionComplete && onActionComplete();
		} catch (error: any) {
		} finally {
			setShowLoading(false);
		}
	};

	return (
		<BootstrapDialog
			open={open}
			onClose={onClose}
			aria-labelledby="customized-dialog-title"
			maxWidth="md"
			fullWidth
			sx={{
				...sx,
			}}>
			<DialogTitle>{`Request to Join ${currentCircleName}`}</DialogTitle>
			<DialogContent>
				{!countryEligibility.eligible && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						{getCountryEligibilityMessage(countryEligibility)}
					</Alert>
				)}
				<TextField
					label="Reason ( optional but recommended )"
					variant="outlined"
					fullWidth
					margin="normal"
					multiline
					rows={3}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder="Why do you want to join?"
				/>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onClose}
					variant="contained"
					sx={{
						color: colors.white,
						backgroundColor: colors.persianRed,
						"&:hover": {
							backgroundColor: colors.persianRed,
						},
						width: 150,
					}}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSendJoinRequest}
					disabled={!canJoin}
					sx={{
						color: colors.white,
						backgroundColor: colors.secondPrimary,
						"&:hover": {
							backgroundColor: colors.secondPrimary,
						},
						width: 150,
					}}>
					{showLoading ?
						<LoadingSpinner
							loading={showLoading}
							sx={{ color: colors.white }}
							size={30}
							color={colors.white}
						/>
						: "Send"}
				</Button>
			</DialogActions>
		</BootstrapDialog>
	);
};

export default JoinRequestForm;
